import {typeboxResolver} from '@hookform/resolvers/typebox'
import {announce} from '@react-aria/live-announcer'
import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {Value} from '@sinclair/typebox/value'
import {FieldError, Form, Label, Text, TextField} from 'react-aria-components'
import {Controller, type SubmitHandler, useForm} from 'react-hook-form'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import PasswordInput from '@/components/PasswordInput/PasswordInput'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {changePassword} from '@/services/authenticationService'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import style from '@/views/Account/Profile/Profile.module.scss'

const schema = Type.Object({
  currentPassword: Type.String({
    default: '',
    minLength: 1,
    errorMessage: 'Current password is required',
  }),
  password: Type.String({
    default: '',
    minLength: 8,
    format: 'password',
    errorMessage:
      'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter & one number',
  }),
  confirmPassword: Type.String({
    default: '',
    minLength: 1,
  }),
})

const typecheck = TypeCompiler.Compile(schema)

type Schema = Static<typeof schema>

export default memo(function ChangePassword() {
  const [isUpdating, setIsUpdating] = useState(false)
  const [isUpdated, setIsUpdated] = useState(false)

  const {handleSubmit, control, setError, reset, formState} = useForm<Schema>({
    mode: 'onTouched',
    resolver: typeboxResolver(typecheck),
    defaultValues: Value.Create(schema),
  })

  if (formState.isDirty && isUpdated) {
    setIsUpdated(false)
  }

  const account = useAccountInformationValue()

  const onSubmit: SubmitHandler<Schema> = data => {
    if (data.password !== data.confirmPassword) {
      setError('confirmPassword', {message: 'Passwords do not match'})
      return
    }
    setIsUpdating(true)
    void changePassword({
      password: data.password,
      currentPassword: data.currentPassword,
    })
      .then(result => {
        if (result) {
          if (typeof result === 'string') {
            announce(result, 'assertive')
            setError(
              'password',
              {message: result},
              {
                shouldFocus: true,
              },
            )
          } else {
            setIsUpdated(true)
            reset()
          }
        } else {
          setError(
            'confirmPassword',
            {message: 'Unexpected error, please try again later!'},
            {
              shouldFocus: false,
            },
          )
        }
      })
      .finally(() => {
        setIsUpdating(false)
      })
  }

  return (
    <>
      <h2>Password</h2>
      <Form className={style.Form} onSubmit={handleSubmit(onSubmit)}>
        {/* Hidden email as username field for accessibility. see https://www.chromium.org/developers/design-documents/create-amazing-password-forms/ */}
        <VisuallyHidden
          isStrict
          as='input'
          title='email'
          name='email'
          value={account?.email}
          autoComplete='username'
          aria-hidden
          readOnly
          tabIndex={-1}
        />
        <Controller
          control={control}
          name='currentPassword'
          render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
            <TextField
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              validationBehavior='aria'
              isInvalid={invalid}
              isRequired
              type='password'
              autoComplete='current-password'
              className={style.Field}
            >
              <Label className={style.Label}>Current password</Label>
              <PasswordInput ref={ref} className={clsx(style.Input, style.InputGroup)} />
              {error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name='password'
          render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
            <TextField
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              validationBehavior='aria'
              isInvalid={invalid}
              isRequired
              type='password'
              autoComplete='new-password'
              className={style.Field}
            >
              <Label className={style.Label}>New password</Label>
              <PasswordInput ref={ref} className={clsx(style.Input, style.InputGroup)} />
              {error?.message ? (
                <FieldError className={style.Error}>{error.message}</FieldError>
              ) : (
                <Text slot='description' className={style.Description}>
                  Use 8 characters or more with a mix of uppercase letters, lowercase letters, and
                  numbers
                </Text>
              )}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name='confirmPassword'
          render={({field: {name, value, onChange, onBlur, ref}, fieldState: {invalid, error}}) => (
            <TextField
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              validationBehavior='aria'
              isInvalid={invalid}
              isRequired
              type='password'
              autoComplete='new-password'
              className={style.Field}
            >
              <Label className={style.Label}>Confirm password</Label>
              <PasswordInput ref={ref} className={clsx(style.Input, style.InputGroup)} />
              {!!error?.message && <FieldError className={style.Error}>{error.message}</FieldError>}
            </TextField>
          )}
        />
        <div className={style.SubmitArea}>
          <PrimaryButton type='submit' isPending={isUpdating}>
            {(() => {
              if (isUpdating) {
                return 'Saving...'
              }
              if (isUpdated) {
                return 'Saved'
              }
              return 'Save change'
            })()}
          </PrimaryButton>
        </div>
      </Form>
    </>
  )
})
